/* eslint-disable eqeqeq */
import { handleTabToNext, looseEq } from '../../formKit';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import { AccSubtab, accList } from '../accessoryFieldKit';
import AccessoryMountingPopup, { type SpecialParametersProps } from '../AccessoryMountingPopup';
import { useSectionInternalWalls } from '../internalWalls';
import {
  ComplexMountingPlaneRow,
  ComplexPlaneRows,
  resolveComplexPlane,
} from '../complexMounting';
import { minToFloorClearance, useAccessDoorFloorMinimum } from '../accessDoorFloorClearance';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-2 — ACCESS DOOR form (accessories/access_door/{view/form.js 154,
 * tmpl/form.tmpl 465}, read in full). §51.2 row:
 *  - master_viewport_id ← viewport_params (viewport master; reset-EXEMPT);
 *  - door pairs: non-unique instance hides its row LI (chassis);
 *  - door_ids partner-section validation sweeps (chassis createPosition +
 *    position model doorIds — parity 303);
 *  - only_viewport pairing in changeSelectValue (chassis — parity 311) and
 *    only_viewport/master_viewport_id pair-drop (autoSaveModel — parity 301);
 *  - NO auto-position (exemption list), NO Auto-Position checkboxes;
 *  - mounting sublist non-clickable until width AND height set
 *    (disableMounting — §49.6:735).
 */
const config = ACCESSORY_FORM_CONFIGS.access_door;

/** mounting_popup_special_parameters.tmpl (45 lines — attachment/hinge/swing). */
const AccessDoorSpecialParameters = ({ api, stageModelField, staged }: SpecialParametersProps) => {
  const groups: Array<{ cls: string; label: string; name: string; cmsPath: string }> = [
    { cls: 'special-parameter-attachment', label: 'Attachment', name: 'mounting_id', cmsPath: 'mounting' },
    { cls: 'special-parameter-hinge_side', label: 'Hinge Side', name: 'hinge_side_id', cmsPath: 'hinge_side' },
    { cls: 'special-parameter-swing', label: 'Swing', name: 'swing_id', cmsPath: 'swing' },
  ];
  return (
    <ul>
      {groups.map((g) => {
        const current = staged[g.name] ?? api.model?.get(g.name);
        return (
          <li className={g.cls} key={g.name}>
            <span className="label">{g.label}</span>
            <div>
              {accList(api.cmsAccessory, g.cmsPath).map((row) => (
                <label className="radio" key={String(row.id)}>
                  <input
                    type="radio"
                    name={g.name}
                    value={String(row.id)}
                    checked={current == row.id}
                    className="model_field_popup"
                    onChange={(e) => stageModelField(g.name, parseInt(e.target.value, 10))}
                  />{' '}
                  {String(row.name ?? '')}
                </label>
              ))}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

/** model_field radio group row (attachment / hinge side / swing). */
const RadioGroupRow = ({
  api,
  name,
  label,
  cmsPath,
}: {
  api: ReturnType<typeof useAccessoryForm>;
  name: string;
  label: string;
  cmsPath: string;
}) => {
  const current = api.model?.get(name);
  return (
    <li>
      <div>
        {/* §JSX-WS — `{' '}` after the radio is load-bearing. form.tmpl:313-323
            puts a NEWLINE between the `<input type="radio">` and `{{name}}`, so
            legacy renders a real ~3.2px space and the caption clears the dial;
            JSX drops whitespace containing a newline and the caption butted up
            against it. Nothing is added BETWEEN the two `label.radio`s — the
            second is `float: right` (ahu/styles.css:2835) so legacy's newline
            there does not render (measured: both captions start at the same x
            in legacy and in the port). */}
        <div className="checkboxes-group access-door">
          {accList(api.cmsAccessory, cmsPath).map((row) => (
            <label className="radio" key={String(row.id)}>
              <input
                type="radio"
                name={name}
                value={String(row.id)}
                checked={current == row.id}
                className="model_field"
                tabIndex={-1}
                onChange={(e) => api.commitModelField(name, e.target.value)}
              />{' '}
              {String(row.name ?? '')}
            </label>
          ))}
        </div>
        <span className="label">{label}</span>
      </div>
    </li>
  );
};

/** viewport_params select row (§49.4 binding, viewport sub-catalog). */
const ViewportSelect = ({
  api,
  name,
  label,
  cmsPath,
  labelKey,
  valueKey,
  rowClass = 'single-line',
  unit,
}: {
  api: ReturnType<typeof useAccessoryForm>;
  name: string;
  label: string;
  cmsPath: string;
  labelKey?: string;
  valueKey?: string;
  rowClass?: string;
  unit?: string;
}) => {
  const engine = api.catalogueFields.master_viewport_id;
  const value = engine?.paramValue(name);
  const options = accList(api.cmsAccessory, cmsPath);
  const ovalue = (o: Record<string, unknown>) => String(valueKey ? o[valueKey] : o.id);
  const current = options.find((o) => looseEq(valueKey ? o[valueKey] : o.id, value));
  return (
    <li>
      <div className={rowClass}>
        {/* §JSX-WS — Size is the `first-two-line` row, where the select's
            45px margin is zeroed and the unit span is the only thing in flow
            ahead of it, so the legacy newline after </span> is 4px of real
            offset (tmpl:401-410). */}
        {unit && (
          <>
            <span className="unit">{unit}</span>{' '}
          </>
        )}
        <span className="selection">
          {current ? <span>{String(current[labelKey ?? 'name'] ?? '')}</span> : null}
        </span>{' '}
        <select
          name={name}
          className="viewport_params_field"
          required
          value={value == null || value === '' ? 'None' : String(value)}
          onChange={(e) => api.commitParamsField('master_viewport_id', name, e.target.value)}
        >
          <option value="None">- -</option>
          {options.map((o) => (
            <option key={ovalue(o)} value={ovalue(o)}>
              {String(o[labelKey ?? 'name'] ?? '')}
            </option>
          ))}
        </select>{' '}
        <span className="label">{label}</span>
      </div>
    </li>
  );
};

const AccessDoorForm = ({ row, section, tunnel, cms, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting', 'viewport'],
  });
  const cmsDoc = cms as Record<string, unknown> | undefined;
  const { internalWalls, mountingPlanesOnCabinet } = useSectionInternalWalls(
    Number(api.model?.get('section_id') ?? section.id),
    api.cmsAccessory
  );
  api.setInternalWalls(internalWalls); // chassis draw-effect IW feed (parity 334)
  // Resolved BEFORE the loading/partner-door early returns below: the To Floor
  // minimum runs an effect, so its hook must be called on every render.
  const resolved = resolveComplexPlane(api, internalWalls, mountingPlanesOnCabinet);
  const floorMin = useAccessDoorFloorMinimum(api, resolved.planeValue);
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  // non-unique partner doors render nothing (chassis hides the row LI too).
  if (model.get('unique') === false) return <div ref={(el) => { api.formRef.current = el; }} />;

  const cid = row.id;
  const viewportEngine = api.catalogueFields.master_viewport_id;
  const widthValues = (api.cmsAccessory?.width_values as unknown[]) ?? [];
  const widthNames = (api.cmsAccessory?.width_value_names as unknown[]) ?? [];
  const heightValues = (api.cmsAccessory?.height_values as unknown[]) ?? [];
  const heightNames = (api.cmsAccessory?.height_value_names as unknown[]) ?? [];
  const linerSheets = accList(cmsDoc, 'casing.liner_metal_sheets');
  const disableMounting = model.get('width') != null && model.get('height') != null;
  const viewportsCount = viewportEngine?.cmsCount;

  const dimSelect = (
    name: 'width' | 'height',
    values: unknown[],
    names: unknown[]
  ) => {
    const value = model.get(name);
    const idx = values.findIndex((v) => looseEq(v, value));
    return (
      <span>
        <span className="selection" style={{ marginLeft: 0 }}>
          {idx !== -1 ? <span>{String(names[idx] ?? '')}</span> : null}
        </span>{' '}
        <select
          name={name}
          className="model_field number_value  medium-input"
          required
          value={value == null ? 'None' : String(value)}
          onChange={(e) => api.commitModelField(name, e.target.value)}
        >
          {idx === -1 && (
            <option value="None" className="hide">
              - -
            </option>
          )}
          {values.map((v, i) => (
            <option key={String(v)} value={String(v)}>
              {String(names[i] ?? '')}
            </option>
          ))}
        </select>
      </span>
    );
  };

  /* §JSX-WS — `.multiply-sign` carries no horizontal padding, so the only
     thing separating the two controls from the `x` in dev is the legacy
     newline (tmpl:71-77). Dropping all four spaces pulled the pair ~4/8/12px
     left and closed the gap either side of the sign. */
  const readonlyPair = (label: string, nameA: string, nameB: string) => (
    <li>
      <div className="multiply">
        <span className="unit">in</span>{' '}
        <input
          data-formatter_type="decimal"
          type="text"
          autoComplete="off"
          name={nameA}
          className="position_field medium-input model non-clickable"
          readOnly
          tabIndex={-1}
          value={String(model.get(nameA) ?? '')}
          onChange={() => undefined}
        />{' '}
        <span className="multiply-sign multiply-sign-11">x</span>{' '}
        <input
          data-formatter_type="decimal"
          type="text"
          autoComplete="off"
          name={nameB}
          className="position_field medium-input model non-clickable"
          readOnly
          tabIndex={-1}
          value={String(model.get(nameB) ?? '')}
          onChange={() => undefined}
        />{' '}
        <span className="label pl-18">{label}</span>
      </div>
    </li>
  );

  return (
    <>
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist">
          <AccSubtab api={api} type="access_door" cid={cid} name="properties" sublistClass="max-height-auto">
            <li>
              <div>
                <input
                  type="text"
                  name="casing_mtl_gauge"
                  className="model_field"
                  value={String(model.get('casing_mtl_gauge') ?? '')}
                  readOnly
                  tabIndex={-1}
                  onChange={() => undefined}
                />{' '}
                <span className="label">Outer Skin</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="selection">
                  {linerSheets.find((o) => looseEq(o.id, model.get('inside_liner_mtl_gauge_id'))) ? (
                    <span>
                      {String(
                        linerSheets.find((o) =>
                          looseEq(o.id, model.get('inside_liner_mtl_gauge_id'))
                        )?.model ?? ''
                      )}
                    </span>
                  ) : null}
                </span>{' '}
                <select
                  name="inside_liner_mtl_gauge_id"
                  className="model_field"
                  value={
                    model.get('inside_liner_mtl_gauge_id') == null
                      ? 'None'
                      : String(model.get('inside_liner_mtl_gauge_id'))
                  }
                  onChange={(e) => api.commitModelField('inside_liner_mtl_gauge_id', e.target.value)}
                >
                  <option value="None" className="hide">
                    - -
                  </option>
                  {linerSheets.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.model ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">Inner Liner</span>
              </div>
            </li>
            <li>
              <div className="multiply">
                <span className="label">Width x Height</span>
              </div>
            </li>
            <li>
              <div className="multiply">
                <span className="unit">in</span>{' '}
                {dimSelect('width', widthValues, widthNames)}{' '}
                <span className="multiply-sign multiply-sign-11">x</span>{' '}
                {dimSelect('height', heightValues, heightNames)}{' '}
                <span className="label pl-18">Nominal</span>
              </div>
            </li>
            {readonlyPair('Actual Size', 'door_width', 'door_height')}
            {readonlyPair('Frame Clear Opening', 'clear_width', 'clear_height')}
          </AccSubtab>

          <AccSubtab
            api={api}
            type="access_door"
            cid={cid}
            name="mounting"
            label="Mounting"
            sublistClass={disableMounting ? undefined : 'non-clickable'}
          >
            <ComplexMountingPlaneRow
              api={api}
              cabinetPlanes={mountingPlanesOnCabinet as Array<Record<string, unknown> & { id: number }>}
              internalWalls={internalWalls}
              selectionText={resolved.planeName}
              selectedValue={
                api.position && Number(api.position.get('mounting_plane_id'))
                  ? String(api.position.get('mounting_plane_id'))
                  : 'None'
              }
              planeLabelRequired
            />
            <ComplexPlaneRows
              api={api}
              variant="access_door"
              planeValue={resolved.planeValue}
              iwPlane={resolved.iwPlane}
              floorMin={floorMin}
            />
            <RadioGroupRow api={api} name="mounting_id" label="Attachment" cmsPath="mounting" />
            <RadioGroupRow api={api} name="hinge_side_id" label="Hinge Side" cmsPath="hinge_side" />
            <RadioGroupRow api={api} name="swing_id" label="Swing" cmsPath="swing" />
          </AccSubtab>

          <AccSubtab
            api={api}
            type="access_door"
            cid={cid}
            name="viewport"
            label="Viewport"
            headerChrome={
              <div className="form-heading" style={{ width: 'auto', zIndex: 5 }}>
                <div className="ahu-header-btns sync-result">
                  <button
                    type="button"
                    name="viewport"
                    data-name="viewport"
                    className={`btn btn-sync vis-viewport${!viewportsCount ? ' warning' : ''}${
                      viewportsCount == 1 || viewportsCount == 0 ? ' synced allow_reset' : ''
                    } clickable`}
                    onClick={() => void viewportEngine?.toggleSmartList()}
                  >
                    <span className="viewports_search_result search-result">
                      {`${viewportsCount ?? ''} / ${viewportEngine?.cmsFullCount ?? ''}`}
                    </span>
                    <i className="icon-list btn-icon-sync">Smartlist</i>
                  </button>
                  <span
                    {...{ tooltip: 'Reset' }}
                    className="icon-refresh-1 ka-has-tooltip viewports_reset reset-btn"
                    onClick={() => viewportEngine?.deselectModel()}
                  >
                    Reset
                  </span>
                </div>
              </div>
            }
          >
            <ViewportSelect api={api} name="shape_id" label="Shape" cmsPath="viewport.shape" />
            <ViewportSelect
              api={api}
              name="size"
              label="Size"
              cmsPath="viewport.size"
              labelKey="size"
              valueKey="size"
              rowClass="first-two-line"
              unit="in"
            />
            <ViewportSelect api={api} name="seal_id" label="Pane" cmsPath="viewport.seal" />
            <ViewportSelect api={api} name="mfg_id" label="Manufacturer" cmsPath="viewport.manufacturer" />
            <ViewportSelect
              api={api}
              name="model"
              label="Model"
              cmsPath="viewport.model"
              labelKey="model"
              valueKey="model"
            />
          </AccSubtab>
        </ul>
      </form>
      <div id={`popup_accessory_mounting_${row.id}`} className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
      <AccessoryMountingPopup
        api={api}
        typeName="access door"
        typeValue="access_door"
        SpecialParameters={AccessDoorSpecialParameters}
        /* The popup stages its own plane, so it gets the RULE, not the number
           the Data Bar row uses (which follows the saved plane). */
        floorMinFor={(stagedPlane) =>
          model.get('unique') === false
            ? null
            : minToFloorClearance(model.get('clearances'), stagedPlane)
        }
      />
    </>
  );
};

export default AccessDoorForm;
